import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { OnsiteCampaign, OnsiteCampaignType, ReferralTypes } from 'app/campaign/campaign.model';
import { CampaignService } from 'app/shared/services/campaign.service';
import { CampaignTemplate, TemplateStatus } from 'app/campaign/templates/template.model';
import { WidgetService } from 'app/shared/services/widget.service';
import { Logger, LoggerFactory } from 'app/core/logger.service';
import { IndeterminateProgressDialogComponent } from 'app/shared/progress/indeterminate-progress-dialog/indeterminate-progress-dialog.component';
import { FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';

@Component({
  selector: 'app-flyout-referral-campaigns',
  templateUrl: './flyout-referral-campaigns.component.html',
  styleUrls: ['./flyout-referral-campaigns.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FlyoutReferralCampaignsComponent implements OnInit {
  public referringTemplates: Array<CampaignTemplate>;
  public friendsTemplates: Array<CampaignTemplate>;
  private readonly logger: Logger;
  public loadingWidgetTemplates: boolean;
  public accordionStep = 'nameReferral';
  public ReferralTypes = ReferralTypes;
  private progressDialogRef: MatDialogRef<IndeterminateProgressDialogComponent>;
  public disabledFriends = false;
  private _openingData: FlyoutOpeningData;
  private referringCampaignResp: OnsiteCampaign;
  public campaignNameValidationError: string;
  public nameReferral = '';
  @Input()
  public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this._openingData = openingData;
    this.disabledFriends = !this._openingData?.campaignReferral;
    if (this._openingData.subType === ReferralTypes.Friends) {
      this.accordionStep = ReferralTypes.Friends;
      this.nameReferral = this._openingData?.campaignReferral?.name;
    }
  }

  constructor(
    private readonly widgetService: WidgetService,
    public readonly loggerFactory: LoggerFactory,
    private readonly campaignService: CampaignService,
    private readonly flyoutService: FlyoutService,
    private dialog: MatDialog,
    private translateService: TranslateService,
  ) {
    this.logger = loggerFactory.get('FlyoutReferralComponent');
  }

  ngOnInit() {
    this.getTemplates();
  }

  public getTemplates(): void {
    this.loadingWidgetTemplates = true;
    this.widgetService.getAllWidgetTemplates().subscribe(
      (templates: Array<CampaignTemplate>) => {
        if (templates) {
          const publishedTemplates = templates
            .filter((template) => template.status === TemplateStatus.Published)
            .sort((a, b) => a.order - b.order);
          this.referringTemplates = publishedTemplates.filter((template) =>
            template.tagsDetails.some((tag) => tag.name.toLowerCase() === ReferralTypes.Referring),
          );
          this.friendsTemplates = publishedTemplates.filter((template) =>
            template.tagsDetails.some((tag) => tag.name.toLowerCase() === ReferralTypes.Friends),
          );
        }
        this.loadingWidgetTemplates = false;
      },
      (error) => {
        this.logger.error('Error that should break the page: ' + JSON.stringify(error));
        this.loadingWidgetTemplates = false;
      },
    );
  }

  public setAccordionStep(value): void {
    this.accordionStep = value;
  }

  public createCampaignFromTemplate(widgetTemplateId: number, type: ReferralTypes): void {
    if (!this.validateCampaignName()) return;
    if (type === ReferralTypes.Friends && this.disabledFriends) return;
    if (type === ReferralTypes.Referring && !this.disabledFriends) return;
    this.showCampaignProgressDialog();
    this.campaignService
      .createOnsiteCampaignFromTemplate(
        widgetTemplateId,
        OnsiteCampaignType.Regular,
        this._openingData.currentAccount.defaultFontFamily,
        OnsiteCampaignType.Regular,
        null,
        this._openingData.currentAccount.isPoweredByDisabled,
        this._openingData.currentAccount.isAttributionActivated,
      )
      .subscribe(
        (campaign: OnsiteCampaign) => {
          if (campaign) {
            this.logger.debug('Campaign after creation: ' + JSON.stringify(campaign));
            if (type === ReferralTypes.Referring) {
              this.campaignService.createReferralCampaign(campaign.id, this.getNameReferral).subscribe(
                (referringCampaign) => {
                  this.referringCampaignResp = referringCampaign;
                  this.disabledFriends = !this.disabledFriends;
                  this.accordionStep = ReferralTypes.Friends;
                  const referringName = this.getNameReferral + ' ' + '(Referrer)';
                  this.saveCampaignName(campaign, referringName).subscribe(() => {
                    this.flyoutService.flyoutUpdatedReferralBS.next({
                      ...referringCampaign,
                      name: referringName,
                    });
                  });
                },
                (error) => {
                  this.logger.error('Error that should break the page: ' + JSON.stringify(error));
                  this.closeCampaignProgressDialog();
                },
              );
            } else if (type === ReferralTypes.Friends) {
              this.updateReferralCampaign(campaign);
            }
            this.closeCampaignProgressDialog();
          }
        },
        (error) => {
          this.logger.error('Error that should break the page: ' + JSON.stringify(error));
          this.closeCampaignProgressDialog();
        },
      );
  }

  public updateReferralCampaign(campaign): void {
    let updatingRefferalCampain;
    if (this._openingData.campaignReferral) {
      this._openingData.campaignReferral.friendCampaign = {};
      this._openingData.campaignReferral.friendCampaign.id = campaign.id;
      updatingRefferalCampain = this._openingData.campaignReferral;
    } else {
      this.referringCampaignResp.friendCampaign = {};
      this.referringCampaignResp.friendCampaign.id = campaign.id;
      updatingRefferalCampain = this.referringCampaignResp;
    }
    this.campaignService.updateReferralCampaign(updatingRefferalCampain).subscribe(
      (campaignReferral) => {
        const friendName =
          this.getNameReferral +
          ' ' +
          `(${this.translateService.instant('referral-campaign.friend-in-name-campaign')})`;
        this.saveCampaignName(campaign, friendName).subscribe(() => {
          this.flyoutService.flyoutUpdatedReferralBS.next({
            ...campaignReferral,
            name: friendName,
          });
          this.cancelFlyout();
        });
      },
      (error) => {
        this.logger.error('Error that should break the page: ' + JSON.stringify(error));
        this.closeCampaignProgressDialog();
      },
    );
  }

  public cancelFlyout(): void {
    this.flyoutService.closeReferralFlyout(undefined);
  }

  private showCampaignProgressDialog(): void {
    this.progressDialogRef = this.dialog.open(IndeterminateProgressDialogComponent, {
      panelClass: 'transparent',
      disableClose: true,
    });
  }

  private closeCampaignProgressDialog(): void {
    if (this.progressDialogRef) {
      this.progressDialogRef.close();
    }
  }

  get getNameReferral(): string {
    return this.nameReferral.trim();
  }

  public validateCampaignName(): boolean {
    const namePattern = /^([0-9A-Za-z�����Œ'()\\?!&\/%;:+<>=\-_\s\.])+$/;
    if (!this.nameReferral.trim()) {
      this.campaignNameValidationError = 'campaign.settings.basic.campaign-name-required';
      return false;
    }
    if (!namePattern.test(this.nameReferral)) {
      this.campaignNameValidationError = 'campaign.settings.basic.campaign-name-pattern';
      return false;
    }
    if (this.nameReferral.trim().length > 100) {
      this.campaignNameValidationError = 'campaign.settings.basic.campaign-name-maxlength';
      return false;
    }
    this.campaignNameValidationError = undefined;
    return true;
  }

  // Completes even when the rename fails, so callers report the half after it and listeners never reload too early.
  public saveCampaignName(campaign: OnsiteCampaign, name: string): Observable<unknown> {
    return this.campaignService
      .updateCampaignWithPatch(
        campaign,
        this.campaignService.getPatchOperations({ id: campaign.id, name: name } as OnsiteCampaign),
      )
      .pipe(
        catchError((error) => {
          this.logger.error(`Could not save campaign name: ${error}`);
          return of(undefined);
        }),
      );
  }
}
